import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Sparkles, Menu, X, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { t, getLocalizedUrl } = useLanguage();

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: userRole } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle();

    setIsAdmin(!!userRole);
  };

  return (
    <nav className="border-b border-white/[0.08] bg-[#030303]/95 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to={getLocalizedUrl('/')} className="flex items-center gap-2 font-bold text-xl hover-scale">
          <Sparkles className="h-6 w-6 text-indigo-500" />
          <span className="text-white">FlashySites</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          <Link to={getLocalizedUrl('/pricing')} className="text-sm font-medium text-white/60 hover:text-white transition-all duration-200 relative group">
            {t('nav.pricing')}
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-indigo-500 to-rose-500 transition-all group-hover:w-full" />
          </Link>
          <Link to={getLocalizedUrl('/how-it-works')} className="text-sm font-medium text-white/60 hover:text-white transition-all duration-200 relative group">
            {t('nav.howItWorks')}
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-indigo-500 to-rose-500 transition-all group-hover:w-full" />
          </Link>
          {isAdmin && (
            <Link to={getLocalizedUrl('/admin')} className="text-sm font-medium text-white/60 hover:text-white transition-all duration-200 relative group flex items-center gap-1">
              <Shield className="h-4 w-4" />
              {t('nav.admin')}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-indigo-500 to-rose-500 transition-all group-hover:w-full" />
            </Link>
          )}
          <Link to={getLocalizedUrl('/login')} className="text-sm font-medium text-white/60 hover:text-white transition-all duration-200 relative group">
            {t('nav.login')}
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-indigo-500 to-rose-500 transition-all group-hover:w-full" />
          </Link>
          <LanguageSwitcher />
          <Button asChild className="bg-gradient-to-r from-indigo-500 to-rose-500 hover:from-indigo-600 hover:to-rose-600 border-0 text-white hover-scale">
            <Link to={getLocalizedUrl('/pricing')}>{t('nav.getStarted')}</Link>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <button
            className="p-2 hover:bg-white/[0.05] rounded-lg transition-all duration-200"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-indigo-500" />
            ) : (
              <Menu className="h-6 w-6 text-indigo-500" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-white/[0.08] bg-[#030303]/95 backdrop-blur-sm animate-fade-in">
          <div className="container mx-auto px-4 py-4 space-y-3">
            <Link
              to={getLocalizedUrl('/pricing')}
              className="block text-sm font-medium text-white/60 hover:text-white transition-all duration-200 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.pricing')}
            </Link>
            <Link
              to={getLocalizedUrl('/how-it-works')}
              className="block text-sm font-medium text-white/60 hover:text-white transition-all duration-200 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.howItWorks')}
            </Link>
            {isAdmin && (
              <Link
                to={getLocalizedUrl('/admin')}
                className="block text-sm font-medium text-white/60 hover:text-white transition-all duration-200 py-2 flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Shield className="h-4 w-4" />
                {t('nav.admin')}
              </Link>
            )}
            <Link
              to={getLocalizedUrl('/login')}
              className="block text-sm font-medium text-white/60 hover:text-white transition-all duration-200 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.login')}
            </Link>
            <Button asChild className="w-full bg-gradient-to-r from-indigo-500 to-rose-500 hover:from-indigo-600 hover:to-rose-600 border-0 text-white">
              <Link to={getLocalizedUrl('/pricing')} onClick={() => setMobileMenuOpen(false)}>
                {t('nav.getStarted')}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};
